import React, { useCallback, useEffect, useRef, useState } from "react";

import { serverUrl } from "../../../../config";
import Pagination from "../../../../partials/pagination";

import { Row, Col } from "react-bootstrap";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

const PAGE_SIZE = 32;
const ANY = "";

// The hotels endpoint has no image column — the hero lives in each hotel's
// stored document, which `detailUrl` serves as a static ~40KB file. Cheap
// enough to pull per card and cache, and far lighter than /hotel-detail
// (which also hits hotelOMEBT + ICE Portal live). Insert uses /hotel-detail
// instead, because only that carries the categorised `images` the block reads.
const heroFromStoredDoc = (doc) =>
  doc?.hotel?.content?.image?.binaryContent?.url ||
  doc?.content?.image?.binaryContent?.url ||
  "";

export default function AddHotelSlide(props) {
  const [filterOptions, setFilterOptions] = useState(null);
  const [region, setRegion] = useState(ANY);
  const [country, setCountry] = useState(ANY);
  const [city, setCity] = useState(ANY);
  const [brand, setBrand] = useState(ANY);
  const [service, setService] = useState(ANY);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [hotels, setHotels] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isInserting, setIsInserting] = useState(false);

  const [selected, setSelected] = useState([]);
  // code -> hero image url. Kept across pages and filter changes so paging
  // back and forth doesn't refetch what we already have.
  const [thumbs, setThumbs] = useState({});
  const thumbsRef = useRef({});

  const isSelected = (code) => selected.some((h) => h.tms_hotel_code === code);

  const toggleHotel = (hotel) => {
    setSelected((prev) =>
      prev.some((h) => h.tms_hotel_code === hotel.tms_hotel_code)
        ? prev.filter((h) => h.tms_hotel_code !== hotel.tms_hotel_code)
        : [...prev, hotel],
    );
  };

  // ─── Filter options (one call, drives all five dropdowns) ────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(`${serverUrl}/api/v2/rhg/filter-options?_format=json`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setFilterOptions(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the free-text search so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ─── Hotels ──────────────────────────────────────────────────────────────
  const fetchHotels = useCallback(() => {
    setIsLoading(true);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      page: String(page),
      _format: "json",
    });
    if (region) params.append("region", region);
    if (country) params.append("country", country);
    if (city) params.append("city", city);
    if (brand) params.append("brand", brand);
    if (service) params.append("services", service);
    if (debouncedSearch) params.append("q", debouncedSearch);

    fetch(`${serverUrl}/api/v2/rhg/hotels?${params.toString()}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        setHotels(json?.collection || []);
        setPages(json?.pages || 1);
        setIsLoading(false);
      })
      .catch(() => {
        setHotels([]);
        setIsLoading(false);
      });
  }, [page, region, country, city, brand, service, debouncedSearch]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  // Any filter change puts us back on page 1 — staying on page 9 of a result
  // set that now has two pages shows an empty grid.
  const onFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  // ─── Card thumbnails ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const missing = hotels.filter(
      (h) => h.detailUrl && thumbsRef.current[h.tms_hotel_code] === undefined,
    );
    if (!missing.length) return undefined;

    // Mark as in-flight up front so a re-render mid-fetch doesn't queue the
    // same hotel twice.
    missing.forEach((h) => {
      thumbsRef.current[h.tms_hotel_code] = "";
    });

    Promise.all(
      missing.map((h) =>
        fetch(h.detailUrl)
          .then((r) => (r.ok ? r.json() : null))
          .then((doc) => [h.tms_hotel_code, heroFromStoredDoc(doc)])
          .catch(() => [h.tms_hotel_code, ""]),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      pairs.forEach(([code, url]) => {
        thumbsRef.current[code] = url;
      });
      setThumbs({ ...thumbsRef.current });
    });

    return () => {
      cancelled = true;
    };
  }, [hotels]);

  // ─── Cascading options ───────────────────────────────────────────────────
  const regions = filterOptions?.regions || [];
  const countries = (filterOptions?.countries || []).filter(
    (c) => !region || c.region === region,
  );
  const cities = (filterOptions?.cities || []).filter(
    (c) =>
      (!country || c.country === country) && (!region || c.region === region),
  );
  const brands = filterOptions?.brands || [];
  const services = filterOptions?.services || [];

  const clearAll = () => setSelected([]);
  const selectAllOnPage = () =>
    setSelected((prev) => {
      const next = [...prev];
      hotels.forEach((h) => {
        if (!next.some((s) => s.tms_hotel_code === h.tms_hotel_code)) {
          next.push(h);
        }
      });
      return next;
    });

  const onInsert = () => {
    if (!selected.length || isInserting) return;
    setIsInserting(true);
    Promise.resolve(props.onAddHotelSlides(selected))
      .then(() => setIsInserting(false))
      .catch(() => setIsInserting(false));
  };

  const renderCard = (hotel, keyPrefix) => {
    const code = hotel.tms_hotel_code;
    const hero = thumbs[code];
    const active = isSelected(code);
    return (
      <Col
        xs={6}
        sm={4}
        md={3}
        xl={2}
        key={`${keyPrefix}-${code}`}
        className="mb-4"
      >
        <Box
          className="hotel-slide-card"
          onClick={() => toggleHotel(hotel)}
          sx={{
            cursor: "pointer",
            border: active ? "3px solid" : "1px solid",
            borderColor: active ? "primary.main" : "#d8d8d8",
            borderRadius: 1,
            overflow: "hidden",
            height: "100%",
            transition: "border-color 0.15s ease-in-out",
          }}
        >
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 10",
              backgroundColor: "#eceff1",
              backgroundImage: hero ? `url("${hero}")` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Box sx={{ padding: "8px 10px 12px" }}>
            {/* Not adminText.light — on the white card that renders almost
                invisible. The location line is a real label, not a hint. */}
            <Typography
              variant="caption"
              owner="admin"
              sx={{ display: "block", color: "#5f6b76" }}
            >
              {[hotel.city, hotel.country].filter(Boolean).join(", ")}
            </Typography>
            <Typography
              variant="body2"
              owner="admin"
              color="adminText.dark"
              // Clamped so a long hotel name can't stretch one card and
              // knock the whole row out of alignment.
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: "3.75em",
              }}
            >
              {hotel.name}
            </Typography>
          </Box>
        </Box>
      </Col>
    );
  };

  const selectLabel = (label, value, onChange, children) => (
    <Col xs={6} md={4} lg="auto" className="mb-2">
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value} onChange={onChange}>
          <MenuItem value={ANY}>Any</MenuItem>
          {children}
        </Select>
      </FormControl>
    </Col>
  );

  return (
    <Dialog
      fullScreen={true}
      open={props.open}
      onClose={() => {
        if (!isInserting) props.onClose();
      }}
      className="presentation-library-modal hotel-slide-library-modal"
    >
      <AppBar
        sx={{
          position: "relative",
          backgroundColor: "primary.light",
          color: "solid.dark",
        }}
      >
        <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Row className="w-100">
              <Col>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Dynamic Hotel Slide Library
                </Typography>
              </Col>
              <Col className="d-flex justify-content-end align-items-center">
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="cancel"
                    disabled={isInserting}
                    onClick={() => props.onClose()}
                    sx={{ marginRight: "10px !important" }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Cancel
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="save"
                    className="lh"
                    disabled={!selected.length || isInserting}
                    startIcon={
                      isInserting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    onClick={onInsert}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      {isInserting
                        ? "Inserting…"
                        : `Insert${selected.length ? ` (${selected.length})` : ""}`}
                    </Typography>
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Toolbar>
        </DialogContent>
      </AppBar>

      <DialogContent>
        <Typography
          variant="h4"
          owner="admin"
          color="adminText.dark"
          className="mb-3"
        >
          Choose hotel slide
        </Typography>

        <Row className="align-items-center mb-3">
          {selectLabel("Region", region, onFilterChange(setRegion),
            regions.map((r) => (
              <MenuItem value={r} key={r}>
                {r}
              </MenuItem>
            )))}
          {selectLabel("Country", country, onFilterChange(setCountry),
            countries.map((c) => (
              <MenuItem value={c.name} key={c.name}>
                {c.name}
              </MenuItem>
            )))}
          {selectLabel("City", city, onFilterChange(setCity),
            cities.map((c) => (
              <MenuItem value={c.name} key={`${c.name}-${c.country}`}>
                {c.name}
              </MenuItem>
            )))}
          {selectLabel("Brand", brand, onFilterChange(setBrand),
            brands.map((b) => (
              <MenuItem value={b.code} key={b.code}>
                {b.name}
              </MenuItem>
            )))}
          {selectLabel("Service", service, onFilterChange(setService),
            services.map((s) => (
              <MenuItem value={s.key} key={s.key}>
                {s.name}
              </MenuItem>
            )))}

          <Col className="d-flex justify-content-end align-items-center">
            <TextField
              size="small"
              label="Search hotels"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{ marginRight: "10px", minWidth: 200 }}
            />
            <Button owner="admin" disabled={!selected.length} onClick={clearAll}>
              <Typography variant="button" owner="admin">
                Clear All
              </Typography>
            </Button>
            <Button owner="admin" disabled={!hotels.length} onClick={selectAllOnPage}>
              <Typography variant="button" owner="admin">
                Select All
              </Typography>
            </Button>
          </Col>
        </Row>

        {isLoading ? (
          <Box className="d-flex justify-content-center py-5">
            <CircularProgress />
          </Box>
        ) : hotels.length ? (
          <Row>{hotels.map((h) => renderCard(h, "grid"))}</Row>
        ) : (
          <Box className="py-5 text-center">
            <Typography variant="body1" owner="admin" color="adminText.light">
              No hotels match those filters.
            </Typography>
          </Box>
        )}

        <Pagination
          owner="admin"
          containerClasses="creator-pagination d-flex justify-content-center align-items-center"
          previousDisabled={page <= 1}
          nextDisabled={page >= pages}
          currentPage={page}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(pages, p + 1))}
        />

        {selected.length > 0 && (
          <Box
            className="mt-4 p-4"
            sx={{ backgroundColor: "#edf7fe", borderRadius: 1 }}
          >
            <Typography
              variant="h5"
              owner="admin"
              color="adminText.dark"
              className="mb-3"
            >
              Selected hotel slides to insert
            </Typography>
            <Row>{selected.map((h) => renderCard(h, "selected"))}</Row>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
